import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'

/* ── Layout ─────────────────────────────────────────────── */

export const Container: FC<{ children: ReactNode, className?: string }> = ({
    children,
    className
}): ReactNode => {
    return (
        <div className={cn('mx-auto w-full max-w-page px-5 sm:px-8 lg:px-12', className)}>
            {children}
        </div>
    )
}

/* ── The setting-out line system ────────────────────────── */

interface EyebrowProps {
    children: ReactNode
    tone?: 'ink' | 'paper'
    className?: string
}

export const Eyebrow: FC<EyebrowProps> = ({
    children,
    tone = 'ink',
    className
}): ReactNode => {
    return (
        <span
            className={cn(
                'inline-flex items-center gap-3 font-mono text-[0.65rem] uppercase tracking-label',
                tone === 'ink' ? 'text-muted-foreground' : 'text-ink-muted',
                className
            )}
        >
            <span aria-hidden='true' className='h-px w-7 bg-minium' />
            {children}
        </span>
    )
}

interface DimensionRuleProps {
    label?: string
    tone?: 'ink' | 'paper'
    className?: string
}

/* A hairline with end ticks and a mono note, borrowed from a dimension line
   on a working drawing. Used to separate movements on a page. */
export const DimensionRule: FC<DimensionRuleProps> = ({
    label,
    tone = 'ink',
    className
}): ReactNode => {
    const line = tone === 'ink' ? 'bg-rule' : 'bg-ink-line'
    const text = tone === 'ink' ? 'text-muted-foreground' : 'text-ink-muted'

    return (
        <div aria-hidden='true' className={cn('flex items-center gap-4', className)}>
            <span className={cn('h-2.5 w-px', line)} />
            <span className={cn('h-px flex-1', line)} />

            {label ? (
                <span className={cn('font-mono text-[0.6rem] uppercase tracking-label', text)}>
                    {label}
                </span>
            ) : null}

            <span className={cn('h-px flex-1', line)} />
            <span className={cn('h-2.5 w-px', line)} />
        </div>
    )
}

interface SectionHeadProps {
    eyebrow?: string
    title: ReactNode
    lead?: ReactNode
    tone?: 'ink' | 'paper'
    action?: ReactNode
    className?: string
}

export const SectionHead: FC<SectionHeadProps> = ({
    eyebrow,
    title,
    lead,
    tone = 'ink',
    action,
    className
}): ReactNode => {
    return (
        <div className={cn('flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between', className)}>
            <div className='max-w-2xl'>
                {eyebrow ? <Eyebrow tone={tone}>{eyebrow}</Eyebrow> : null}

                <h2
                    className={cn(
                        'mt-5 font-heading text-3xl font-extrabold tracking-display sm:text-4xl lg:text-5xl',
                        tone === 'ink' ? 'text-ink' : 'text-paper'
                    )}
                >
                    {title}
                </h2>

                {lead ? (
                    <p
                        className={cn(
                            'mt-5 max-w-xl text-base sm:text-lg',
                            tone === 'ink' ? 'text-muted-foreground' : 'text-ink-muted'
                        )}
                    >
                        {lead}
                    </p>
                ) : null}
            </div>

            {action ? <div className='shrink-0'>{action}</div> : null}
        </div>
    )
}

/* ── Actions ────────────────────────────────────────────── */

type ButtonVariant = 'primary' | 'ink' | 'outline' | 'outlineLight'

interface ButtonLinkProps {
    href: string
    children: ReactNode
    variant?: ButtonVariant
    size?: 'md' | 'lg'
    withArrow?: boolean
    className?: string
    ariaLabel?: string
}

const variants: Record<ButtonVariant, string> = {
    primary: 'bg-minium text-white hover:bg-minium-hover border-minium hover:border-minium-hover',
    ink: 'bg-ink text-paper hover:bg-ink-raised border-ink hover:border-ink-raised',
    outline: 'border-ink/25 text-ink hover:border-ink hover:bg-ink hover:text-paper',
    outlineLight: 'border-ink-line text-paper hover:bg-paper hover:text-ink hover:border-paper'
}

export const ButtonLink: FC<ButtonLinkProps> = ({
    href,
    children,
    variant = 'primary',
    size = 'md',
    withArrow = true,
    className,
    ariaLabel
}): ReactNode => {
    const classes = cn(
        'group inline-flex items-center justify-center gap-2.5 rounded-sm border font-medium',
        'transition-colors duration-200 ease-out',
        size === 'lg' ? 'px-7 py-4 text-[0.95rem]' : 'px-5 py-3 text-sm',
        variants[variant],
        className
    )

    const inner = (
        <>
            <span>{children}</span>

            {withArrow ? (
                <ArrowRight
                    aria-hidden='true'
                    className='size-4 transition-transform duration-200 ease-out group-hover:translate-x-1'
                />
            ) : null}
        </>
    )

    const isExternal = /^(https?:|tel:|mailto:)/.test(href)

    if (isExternal) {
        return (
            <a href={href} className={classes} aria-label={ariaLabel}>
                {inner}
            </a>
        )
    }

    return (
        <Link href={href} className={classes} aria-label={ariaLabel}>
            {inner}
        </Link>
    )
}

interface TextLinkProps {
    href: string
    children: ReactNode
    tone?: 'ink' | 'paper'
    className?: string
}

export const TextLink: FC<TextLinkProps> = ({
    href,
    children,
    tone = 'ink',
    className
}): ReactNode => {
    return (
        <Link
            href={href}
            className={cn(
                'group inline-flex items-center gap-2 font-medium transition-colors',
                tone === 'ink'
                    ? 'text-ink hover:text-minium-deep'
                    : 'text-paper hover:text-minium-tint',
                className
            )}
        >
            <span className='border-b border-minium pb-0.5'>{children}</span>

            <ArrowRight
                aria-hidden='true'
                className='size-4 transition-transform duration-200 ease-out group-hover:translate-x-1'
            />
        </Link>
    )
}
